import { useRef, useState } from 'react';
import { ACCEPT, MAX_FILES, addPicks, fileHref, isPdf, sizeLabel } from '../lib/ticketFiles.js';

/* Picking files and showing them, for FILED tickets (S09). The rules and the sending are in
 * lib/ticketFiles.js; the server decides again. Demo tickets keep their own picture box. */

const iconFor = (f) => (isPdf(f) ? 'picture_as_pdf' : 'image');

/* Choose up to five pictures or PDFs. Nothing is sent here: the form sends them after the ticket or
 * the reply is saved. `picks` is the list lib/ticketFiles.addPicks builds. */
export function FilePicker({ picks, onChange, disabled = false, label = 'A screenshot or file', hint }) {
  const inputRef = useRef(null);
  const [refused, setRefused] = useState([]);
  const full = picks.length >= MAX_FILES;

  function onFiles(list) {
    const out = addPicks(picks, list);
    setRefused(out.refused);
    onChange(out.picks);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div className="field">
      <span className="field__label">
        {label} <span className="opt">(optional)</span>
      </span>
      <button type="button" className="dropzone" onClick={() => inputRef.current?.click()} disabled={disabled || full}>
        <span className="material-symbols-outlined" aria-hidden="true">attach_file</span>
        {full ? `That is ${MAX_FILES}, the most` : 'Choose a picture or PDF'}
      </button>
      <input ref={inputRef} type="file" accept={ACCEPT} multiple hidden onChange={(e) => onFiles(e.target.files)} />
      <span className="field__hint">{hint || `Pictures or PDF, up to ${MAX_FILES} files, 10 MB each.`}</span>
      {refused.length ? (
        <div className="why" role="alert">
          {refused.map((r) => <div key={r}>{r}</div>)}
        </div>
      ) : null}
      {picks.length ? (
        <ul className="filelist">
          {picks.map((p) => (
            <li key={p.key} className="filelist__row">
              <span className="material-symbols-outlined" aria-hidden="true">{iconFor(p.file)}</span>
              <span className="filelist__name">{p.name}</span>
              <small>{sizeLabel(p.size)}</small>
              <button
                type="button"
                className="filelist__x"
                aria-label={`Remove ${p.name}`}
                disabled={disabled}
                onClick={() => onChange(picks.filter((x) => x.key !== p.key))}
              >
                <span className="material-symbols-outlined" aria-hidden="true">close</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/* The files on a ticket or a message, as the server listed them for this reader. A file deleted
 * after the ticket closed stays in the list, with what happened to it. Opening one downloads it:
 * the server always sends a file as a download, never as a page. */
export function FileList({ ticketNo, files }) {
  if (!files || files.length === 0) return null;
  return (
    <ul className="filelist">
      {files.map((f) => (
        <li key={f.id} className="filelist__row">
          <span className="material-symbols-outlined" aria-hidden="true">{f.deleted ? 'delete' : iconFor(f)}</span>
          {f.deleted ? (
            <span className="filelist__name muted">{f.name} (deleted after the ticket closed)</span>
          ) : (
            <a className="filelist__name" href={fileHref(ticketNo, f.id)} download={f.name}>{f.name}</a>
          )}
          {f.deleted ? null : <small>{sizeLabel(f.size)}</small>}
        </li>
      ))}
    </ul>
  );
}
